import React from 'react';
import { createPortal } from 'react-dom';
import { describe, type ValidationMap, useOwnerProps } from '@elliemae/ds-props-helpers';
import { StyledFloatingAnimation, StyledFloatingWrapper } from './styled.js';
import { type DSFloatingWrapperT, DSFloatingWrapperPropTypes } from './react-desc-prop-types.js';
import { useFloatingWrapper } from './config/useFloatingWrapper.js';
import { DSFloatingWrapperName } from '../../constants/index.js';
import { getDocumentMainLandmarkOrBody } from '../../utils/getDocumentMainLandmarkOrBody.js';

type ContentComponentPropsT = {
  xstyledProps: ReturnType<typeof useFloatingWrapper>['xstyledProps'];
  globalsAttrs: ReturnType<typeof useFloatingWrapper>['globalsAttrs'];
  ownerPropsConfig: {
    getOwnerProps: () => DSFloatingWrapperT.Props;
    getOwnerPropsArguments: () => object;
  };
  handleAnimationEnd: React.AnimationEventHandler<HTMLDivElement>;
  // for performance reasons, it's better for pure component to not receive nested objects
  // so the following is just the typescript way of describing we spread the props
} & Omit<DSFloatingWrapperT.InternalProps, 'context'> &
  DSFloatingWrapperT.InternalProps['context'];

const ContentComponent = React.memo<ContentComponentPropsT>(
  ({
    children,
    innerRef,
    floatingStyles,
    isOpen,
    animationDuration,
    withoutAnimation,
    handleAnimationEnd,
    xstyledProps,
    globalsAttrs,
    ownerPropsConfig,
  }) => (
    <StyledFloatingWrapper
      innerRef={innerRef}
      style={floatingStyles}
      {...xstyledProps}
      data-testid="ds-floating-wrapper-root"
      {...globalsAttrs}
      {...ownerPropsConfig}
    >
      <StyledFloatingAnimation
        onAnimationEnd={handleAnimationEnd}
        isOpen={isOpen}
        animationDuration={animationDuration}
        withoutAnimation={withoutAnimation}
        {...ownerPropsConfig}
      >
        {children}
      </StyledFloatingAnimation>
    </StyledFloatingWrapper>
  ),
);

const FloatingWrapper: React.ComponentType<DSFloatingWrapperT.Props> = (props) => {
  const { propsWithDefault, xstyledProps, globalsAttrs } = useFloatingWrapper(props);
  const { isOpen } = propsWithDefault;
  const ownerPropsConfig = useOwnerProps(props);

  const { context, onAnimationEnd, onAnimationStartTriggered, ...contentProps } = propsWithDefault;
  const { portalDOMContainer, withoutPortal, withoutAnimation } = context;

  const [isAnimating, setIsAnimating] = React.useState(false);
  React.useEffect(() => {
    if (isOpen && !withoutAnimation) {
      setIsAnimating(true);
      onAnimationStartTriggered?.();
    }
  }, [isOpen, onAnimationStartTriggered, withoutAnimation]);

  const handleAnimationEnd = React.useCallback<React.AnimationEventHandler<HTMLDivElement>>(
    (e) => {
      setIsAnimating(false);
      if (onAnimationEnd) onAnimationEnd(e);
    },
    [onAnimationEnd],
  );

  if (isOpen || isAnimating) {
    if (withoutPortal === true)
      return (
        <ContentComponent
          xstyledProps={xstyledProps}
          globalsAttrs={globalsAttrs}
          ownerPropsConfig={ownerPropsConfig}
          {...contentProps}
          {...context}
          handleAnimationEnd={handleAnimationEnd}
        />
      );
    if (!withoutPortal)
      return (
        <>
          {createPortal(
            <ContentComponent
              xstyledProps={xstyledProps}
              globalsAttrs={globalsAttrs}
              ownerPropsConfig={ownerPropsConfig}
              {...contentProps}
              {...context}
              handleAnimationEnd={handleAnimationEnd}
            />,
            portalDOMContainer || getDocumentMainLandmarkOrBody(),
          )}
        </>
      );
  }
  return null;
};

FloatingWrapper.displayName = DSFloatingWrapperName;
const FloatingWrapperWithSchema = describe(FloatingWrapper);
FloatingWrapperWithSchema.propTypes = DSFloatingWrapperPropTypes as unknown as ValidationMap<DSFloatingWrapperT.Props>;
export { FloatingWrapper, FloatingWrapperWithSchema };
